/**
 * Pantalla **Editar pedido de láminas** (`/herramientas/pedidos-laminas/:pedidoId/
 * editar`). Reemplaza el `PlaceholderPage` (no existía ruta React previa). Equivale
 * a `editar_pedido_lamina.html` del SSR (demolido en esta feature).
 *
 * Carga el detalle con `usePedido` (TanStack Query) y prellena el `PedidoForm`
 * (modo `edit`). Editar es **solo admin** y solo pendientes (igual que el SSR/API):
 * el gating de pantalla exige `edit_pedidos_laminas` + rol admin; si el pedido no
 * está pendiente, se informa y no se permite editar (el backend devolvería el mismo
 * error). En éxito navega al tablero. Las sedes origen/destino no se editan (son
 * fijas 39<->41): se muestran solo-lectura. Presentacional + hooks; sin `fetch`.
 */
import { useNavigate, useParams } from "react-router-dom";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useAuth } from "@/lib/auth";
import { ROUTES } from "@/features/navigation";

import { EDIT_PERMISSION } from "../constants";
import { usePedido } from "../hooks/usePedidosBoard";
import { useUpdatePedido } from "../hooks/usePedidoMutations";
import type { PedidoFormValues } from "../schemas";
import type { PedidoDetail, PedidoUpdateBody } from "../types";
import { PedidoForm } from "./PedidoForm";

/** Mapea el detalle del pedido a los valores iniciales del formulario. */
function detailToFormValues(pedido: PedidoDetail): PedidoFormValues {
  return {
    categoria: pedido.categoria ?? "",
    calibre: pedido.calibre ?? "",
    medida: pedido.medida ?? "",
    tubo: pedido.tubo ?? "",
    descripcion: pedido.descripcion ?? "",
    cantidad: String(pedido.cantidad ?? ""),
  };
}

export function EditarPedidoScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const { hasPermission, user } = useAuth();
  const isAdmin = user?.role === "admin";

  const pedidoId = Number(params.pedidoId);
  const validId = Number.isInteger(pedidoId) && pedidoId > 0;
  // Editar es solo admin (el SSR redirige al tablero a un no-admin). El detalle
  // solo se pide si el usuario puede editar: evita un fetch inútil sin permiso.
  const canEdit = hasPermission(EDIT_PERMISSION) && isAdmin;

  const { data, isPending, isError, error } = usePedido(
    canEdit && validId ? pedidoId : null,
  );
  const updatePedido = useUpdatePedido();

  if (!canEdit) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Editar pedido de láminas</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600" role="alert">
            Solo los administradores pueden editar pedidos.
          </p>
        </CardContent>
      </Card>
    );
  }

  function handleSubmit(values: PedidoFormValues) {
    if (!data) {
      return;
    }
    const body: PedidoUpdateBody = {
      categoria: values.categoria.trim(),
      calibre: (values.calibre ?? "").trim(),
      tubo: (values.tubo ?? "").trim(),
      descripcion: (values.descripcion ?? "").trim(),
      cantidad: values.cantidad.trim(),
      origen_sede_id: data.origen_sede_id as number,
      destino_sede_id: data.destino_sede_id as number,
    };
    updatePedido.mutate(
      { id: data.id, body },
      { onSuccess: () => navigate(ROUTES.pedidosLaminas) },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold text-slate-900">
        {data ? `Editar pedido #${data.id}` : "Editar pedido de láminas"}
      </h1>
      <Card>
        <CardContent>
          {!validId ? (
            <p className="text-sm text-red-600" role="alert">
              Pedido no válido.
            </p>
          ) : isPending ? (
            <p className="text-sm text-slate-500" role="status">
              Cargando pedido…
            </p>
          ) : isError ? (
            <p className="text-sm text-red-600" role="alert">
              {error instanceof Error && error.message
                ? error.message
                : "No se pudo cargar el pedido."}
            </p>
          ) : data.estado !== "pendiente" ? (
            <p className="text-sm text-red-600" role="alert">
              Solo se pueden editar pedidos pendientes.
            </p>
          ) : (
            <PedidoForm
              initialValues={detailToFormValues(data)}
              origenNombre={data.origen_sede?.nombre ?? null}
              destinoNombre={data.destino_sede?.nombre ?? null}
              isSubmitting={updatePedido.isPending}
              apiError={updatePedido.isError ? updatePedido.error : null}
              onSubmit={handleSubmit}
              submitLabel="Guardar cambios"
              onCancel={() => navigate(ROUTES.pedidosLaminas)}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
